import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import { Loader2 } from "lucide-react";

function Shell({ title, subtitle, children }) {
  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md panel p-8">
        <div className="flex justify-center mb-6"><Logo compact /></div>
        <h1 className="font-heading font-black uppercase text-3xl text-center text-gradient-gold">{title}</h1>
        {subtitle && <p className="text-center text-slate-400 text-sm mt-1 mb-6">{subtitle}</p>}
        {children}
      </div>
    </div>
  );
}

const inputCls = "w-full bg-black/50 border border-smasher-gold/25 rounded-xl px-4 py-3 text-white outline-none focus:border-smasher-gold transition";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate(new URLSearchParams(window.location.search).get("next") || "/");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Login" subtitle="Welcome back, Smasher.">
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        {error && <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="login-error">{error}</div>}
        <input type="email" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="login-email" />
        <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="login-password" />
        <button disabled={busy} className="btn-gold w-full py-3 rounded-xl font-heading font-black tracking-wide flex items-center justify-center gap-2 disabled:opacity-60" data-testid="login-submit">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} LOGIN
        </button>
      </form>
      <div className="flex justify-between text-sm mt-4 text-slate-400">
        <Link to="/forgot-password" className="hover:text-smasher-gold">Forgot password?</Link>
        <Link to={`/register${window.location.search}`} className="hover:text-smasher-gold">Create account</Link>
      </div>
    </Shell>
  );
}
